import {branchFetcher} from './api';
import {isDepthLimitReached} from './depth';
import {
    RelationsFilters,
    TaxpayerNode,
    TaxpayerNonNpd,
    TaxpayerRelationship,
    TaxpayerRelationshipType,
} from './types';

interface BranchBuilderProps {
    root: TaxpayerNode;
    children: TaxpayerNode[];
    relationships: TaxpayerRelationship[];
    nonNpd: TaxpayerNonNpd;
}

export class BranchBuilder implements BranchBuilderProps {
    root: TaxpayerNode;
    children: TaxpayerNode[];
    relationships: TaxpayerRelationship[];
    nonNpd: TaxpayerNonNpd;

    constructor(props: BranchBuilderProps) {
        this.root = props.root;
        this.children = props.children;
        this.relationships = props.relationships;
        this.nonNpd = props.nonNpd;
    }

    static calculateExpand(root: TaxpayerNode) {
        if (!root.children?.length) return;
        const lastChild = root.children[root.children.length - 1];
        lastChild.forExpand = root.children.length <= root.totalChildrenCount - 1;

        if (root.nonNpd?.count && !lastChild.forExpand && !lastChild.isNonNpd) {
            root.children.push({
                uuid: `${root.uuid}-non-npd`,
                elementId: `${root.elementId}-non-npd`,
                properties: {
                    id: '',
                    name: '',
                    amountBenefit: '0',
                    declarationType: 'NOT_FILED',
                    gapSum: '0',
                    gapType: 'DECLARATION_NOT_FILED',
                    isDecFiled: false,
                    isTaxGap: false,
                    payStatus: 'NOT_PAID',
                    sumNdsDeduction: '0',
                    sumPayNds: '0',
                    surColour: 'RED',
                    tin: '',
                    period: root.properties.period,
                    totalSumNds: '0',
                    transitOrGap: 'OTHER',
                },
                totalChildrenCount: 0,
                nonNpd: {taxAmount: 0, amountPrice: 0, count: 0},
                isNonNpd: true,
            });
        }
    }

    static appendChildren(node: any, items: TaxpayerNode[]) {
        if (!node.data.children) node.data.children = [];
        node.data.children.push(...items);
        BranchBuilder.calculateExpand(node.data);
    }

    static async addBranchTo({
        node,
        type,
        filters,
    }: {
        node: any;
        type: TaxpayerRelationshipType;
        filters: RelationsFilters;
    }) {
        if (!node || node.data.isNonNpd) return;
        if (isDepthLimitReached(node)) return;

        if (!node.data.forExpand) {
            if (!node.data.children) {
                const branchBuilder = await branchFetcher.getBranch({
                    type,
                    tin: node.data.properties.tin,
                    period: node.data.properties.period,
                    exclude: node.parent?.data.properties.tin,
                    size: 6,
                    filters,
                });
                if (!branchBuilder) return;
                const branch = branchBuilder.build();
                if (!branch.children) return;

                node.data.totalChildrenCount = branch.totalChildrenCount;
                node.data.nonNpd = branch.nonNpd;
                BranchBuilder.appendChildren(node, branch.children);
            } else {
                node.data.children = undefined;
            }
        } else {
            node.data.forExpand = false;
            const parentProps = node.parent?.data.properties;
            if (!parentProps) return;

            const branchBuilder = await branchFetcher.getBranch({
                type,
                tin: parentProps.tin,
                period: parentProps.period,
                exclude: parentProps.tin,
                skip: node.parent?.data.children?.length || 0,
                size: 6,
                filters,
            });
            if (!branchBuilder || !node.parent?.data.children) return;
            const branch = branchBuilder.build();
            if (!branch.children) return;
            BranchBuilder.appendChildren(node.parent, branch.children);
        }
    }

    build() {
        this.root.nonNpd = this.nonNpd;
        this.root.children = this.children;
        BranchBuilder.calculateExpand(this.root);

        this.relationships.forEach((rel) => {
            if (rel.endNodeElementId === this.root.elementId) {
                const startNode = this.children.find(
                    (child) => child.elementId === rel.startNodeElementId,
                );
                if (startNode) startNode.relationshipToParent = rel;
            } else {
                const endNode = this.children.find(
                    (child) => child.elementId === rel.endNodeElementId,
                );
                if (endNode) endNode.relationshipFromParent = rel;
            }
        });
        console.log(this.root);
        return this.root;
    }
}
